import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { Task, PERMISSIONS } from '@property/shared';
import { Button } from '@/components/ui/button';
import { formatTimestamp } from '@/lib/date';
import { usePermissions } from '@/hooks/use-permissions';
import { useIsTableMobile } from '@/hooks/use-is-table-mobile';
import { DataTable, DataTableColumnHeader } from '@/components/data-table';
import type {
  DataTablePaginationState,
  DataTableSortingState,
  DataTableEmptyState,
} from '@/components/data-table';
import { dueDateColumn } from '@/components/data-table/columns';
import { ReassignTaskModal } from '@/components/reassign-task-modal';
import { useDeclineTask } from '@/hooks/use-task-mutations';

type TaskTableType = 'open' | 'completed';

interface TaskTableProps {
  tasks: Task[];
  type: TaskTableType;
  isLoading?: boolean;
  isFetching?: boolean;
  pagination: DataTablePaginationState;
  sorting: DataTableSortingState;
  emptyState: DataTableEmptyState;
  onSearchChange?: (search: string) => void;
  onRefresh?: () => void;
}

export function TaskTable({
  tasks,
  type,
  isLoading,
  isFetching,
  pagination,
  sorting,
  emptyState,
  onSearchChange,
  onRefresh,
}: TaskTableProps) {
  const navigate = useNavigate();
  const { can, userId } = usePermissions();
  const isMobile = useIsTableMobile();
  const [reassignTarget, setReassignTarget] = useState<Task | null>(null);
  const { mutate: declineTask, isPending: isDeclining } = useDeclineTask();

  const locationCol: ColumnDef<Task> = {
    accessorKey: 'location',
    enableSorting: false,
    meta: { label: 'Address', isPrimary: true, isMandatory: true, searchable: true },
    header: () => (
      <span className="pl-6 text-xs uppercase tracking-wider font-semibold">Address</span>
    ),
    cell: ({ row }) => {
      const { location } = row.original;
      return (
        <div className="pl-6">
          <div className="text-sm text-muted-foreground line-clamp-1">{location.line1}</div>
          <div className="text-[11px] text-muted-foreground/70 mt-0.5 line-clamp-1">
            {location.city}, {location.state}
          </div>
        </div>
      );
    },
  };

  const ownerCol: ColumnDef<Task> = {
    id: 'ownerName',
    accessorFn: (row) => row.ownerName,
    enableSorting: false,
    meta: { label: 'Owner', defaultHidden: true },
    header: () => <span className="text-sm font-medium">Owner</span>,
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{row.original.ownerName ?? '—'}</span>
    ),
  };

  const openDateCol = dueDateColumn<Task>((row) => row.before, isMobile, {
    id: 'beforeDate',
    label: 'Due Date',
    title: 'Due',
    className: 'w-32',
  });

  const completedDateCol: ColumnDef<Task> = {
    id: 'completedAt',
    accessorFn: (row) => row.completed,
    enableSorting: true,
    meta: { className: 'w-40', label: 'Completed' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Completed" />,
    cell: ({ row }) => {
      const iso = row.original.completed ?? row.original.createdAt;
      return (
        <span className="text-[13px] text-muted-foreground whitespace-nowrap">
          {formatTimestamp(
            iso,
            isMobile ? { month: 'short', day: 'numeric' } : { dateStyle: 'medium' },
          )}
        </span>
      );
    },
  };

  const actionCol: ColumnDef<Task> = {
    id: 'action',
    enableSorting: false,
    meta: { className: 'w-28 sm:w-52', label: 'Action', isMandatory: true },
    header: () => <span className="text-xs uppercase tracking-wider font-semibold">Action</span>,
    cell: ({ row }) => {
      const task = row.original;
      return (
        <div className="flex gap-2">
          {task.type === 'REVIEW' && can(PERMISSIONS.INSPECTIONS_REVIEW) ? (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                navigate({ to: '/dashboard/task/$taskId', params: { taskId: task.id } });
              }}
            >
              Review
            </Button>
          ) : task.status === 'IN_PROGRESS' && task.assignedTo === userId ? (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                navigate({ to: '/dashboard/task/$taskId/report', params: { taskId: task.id } });
              }}
            >
              Continue
            </Button>
          ) : task.status === 'ASSIGNED' && task.assignedTo === userId ? (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate({ to: '/dashboard/task/$taskId/report', params: { taskId: task.id } });
              }}
            >
              Start
            </Button>
          ) : null}
          {type === 'open' && can(PERMISSIONS.TASKS_ASSIGN) && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                setReassignTarget(task);
              }}
            >
              Reassign
            </Button>
          )}
          {type === 'open' &&
            !can(PERMISSIONS.TASKS_ASSIGN) &&
            task.assignedTo === userId &&
            (task.status === 'ASSIGNED' || task.status === 'IN_PROGRESS') && (
              <Button
                size="sm"
                variant="outline"
                disabled={isDeclining}
                onClick={(e) => {
                  e.stopPropagation();
                  declineTask({ taskId: task.id });
                }}
              >
                Reject
              </Button>
            )}
        </div>
      );
    },
  };

  const columns: ColumnDef<Task>[] =
    type === 'open'
      ? [locationCol, ownerCol, openDateCol, actionCol]
      : [locationCol, ownerCol, completedDateCol];

  return (
    <>
      <DataTable
        tableId={`task-table-${type}`}
        showColumnToggle
        onRefresh={onRefresh}
        columns={columns}
        data={tasks}
        isLoading={isLoading}
        isFetching={isFetching}
        pagination={pagination}
        sorting={sorting}
        emptyState={emptyState}
        onSearchChange={onSearchChange}
        getRowId={(row) => row.id}
        onRowClick={(row) =>
          navigate({ to: '/dashboard/task/$taskId', params: { taskId: row.id } })
        }
      />
      <ReassignTaskModal
        task={reassignTarget}
        open={reassignTarget !== null}
        onOpenChange={(open) => {
          if (!open) setReassignTarget(null);
        }}
      />
    </>
  );
}
